import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../config/config';

const DISMISSAL_STORAGE_KEY = 'brandSpotlightDismissed';
const DISMISSAL_DAYS = 7;
const DISMISSAL_MS = DISMISSAL_DAYS * 24 * 60 * 60 * 1000;

function getDismissedMap() {
  try {
    const raw = localStorage.getItem(DISMISSAL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function isBrandDismissed(brandName) {
  const dismissed = getDismissedMap();
  const ts = dismissed[brandName];
  if (ts == null) return false;
  return Date.now() - ts < DISMISSAL_MS;
}

function setBrandDismissed(brandName) {
  const now = Date.now();
  const dismissed = getDismissedMap();
  dismissed[brandName] = now;
  // Prune entries older than DISMISSAL_DAYS so localStorage doesn't grow unbounded
  const pruned = {};
  for (const [name, ts] of Object.entries(dismissed)) {
    if (now - ts < DISMISSAL_MS) pruned[name] = ts;
  }
  localStorage.setItem(DISMISSAL_STORAGE_KEY, JSON.stringify(pruned));
}

/** Day of year 1–366 for rotation */
function getDayOfYear() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start + (start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000;
  return Math.floor(diff / (24 * 60 * 60 * 1000));
}

function BrandSpotlight() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [spotlightBrand, setSpotlightBrand] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [spotlightText, setSpotlightText] = useState({
    title: 'Brand Spotlight',
    subtitle: 'Discover emerging brands from Kashmir',
    ctaText: 'Explore Brand',
  });

  const dayOfYear = useMemo(getDayOfYear, []);

  // Slideshow effect: cycle through brands every 5 seconds
  useEffect(() => {
    if (brands.length <= 1) return; // No slideshow needed for single brand

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % brands.length);
        setIsTransitioning(false);
      }, 300); // Fade out duration
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [brands.length]);

  // Update spotlightBrand when currentIndex changes
  useEffect(() => {
    if (brands.length > 0) {
      setSpotlightBrand(brands[currentIndex]);
    }
  }, [currentIndex, brands]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        // 1. Fetch admin spotlight settings
        let spotlightEnabled = true;
        let adminBrandNames = [];
        let spotlightLimit = 10;
        let spotlightDays = 7;
        let spotlightTitle = 'Brand Spotlight';
        let spotlightSubtitle = 'Discover emerging brands from Kashmir';
        let spotlightCtaText = 'Explore Brand';

        try {
          const settingsRes = await apiClient.get('/spotlight-settings/');
          if (settingsRes.data) {
            spotlightEnabled = settingsRes.data.spotlight_enabled !== false;
            adminBrandNames = settingsRes.data.spotlight_brand_names || [];
            spotlightLimit = settingsRes.data.spotlight_limit || 10;
            spotlightDays = settingsRes.data.spotlight_days || 7;
            spotlightTitle = settingsRes.data.spotlight_title || 'Brand Spotlight';
            spotlightSubtitle = settingsRes.data.spotlight_subtitle || 'Discover emerging brands from Kashmir';
            spotlightCtaText = settingsRes.data.spotlight_cta_text || 'Explore Brand';
            
            // Update state for UI
            setSpotlightText({
              title: spotlightTitle,
              subtitle: spotlightSubtitle,
              ctaText: spotlightCtaText,
            });
          }
        } catch (e) {
          // Settings endpoint unavailable — use defaults
        }

        if (cancelled) return;

        // 2. If admin disabled spotlight, show nothing
        if (!spotlightEnabled) {
          setSpotlightBrand(null);
          setBrands([]);
          setLoading(false);
          return;
        }

        // 3. Build the brand list
        // Priority: admin-specified brands > upcoming launches > auto (newly launched)
        let list = [];

        if (adminBrandNames.length > 0) {
          // Admin specified brands — always use these regardless of upcoming mode
          const adminNamesLower = adminBrandNames.map(n => n.toLowerCase());

          // Try newly-launched first (has richer data like days_since_launch)
          try {
            const newRes = await apiClient.get('/brands/newly-launched/', {
              params: { days: spotlightDays, limit: 50 },
            });
            if (cancelled) return;
            const newList = (Array.isArray(newRes.data) ? newRes.data : []).filter(
              b => b && b.name && adminNamesLower.includes(b.name.toLowerCase())
            );
            if (newList.length > 0) {
              list = newList;
            }
          } catch (e) {
            // ignore
          }

          // If not found in newly-launched, fetch from general brands list
          if (list.length === 0) {
            try {
              const allRes = await apiClient.get('/brands/with-images/');
              if (cancelled) return;
              const allBrands = (Array.isArray(allRes.data) ? allRes.data : []).filter(
                b => b && b.name && adminNamesLower.includes(b.name.toLowerCase())
              );
              list = allBrands.map(b => ({
                ...b,
                days_since_launch: 0,
              }));
            } catch (e) {
              // ignore
            }
          }
        } else {
          // Auto mode: only newly launched brands within the launch window
          const response = await apiClient.get('/brands/newly-launched/', {
            params: { days: spotlightDays, limit: spotlightLimit },
          });
          if (cancelled) return;

          list = (Array.isArray(response.data) ? response.data : []).filter(
            (b) =>
              b &&
              b.name != null &&
              String(b.name).trim() !== '' &&
              (b.days_since_launch ?? 999) <= spotlightDays
          );
        }

        // Apply limit
        list = list.slice(0, spotlightLimit);

        if (list.length === 0) {
          setSpotlightBrand(null);
          setBrands([]);
          setLoading(false);
          return;
        }

        const notDismissed = list.filter((b) => !isBrandDismissed(b.name));
        const candidates = notDismissed.length > 0 ? notDismissed : list;

        // Set all brands for slideshow
        setBrands(candidates);
        // Set initial index (can use day of year for variety)
        const initialIndex = dayOfYear % candidates.length;
        setCurrentIndex(initialIndex);
        setSpotlightBrand(candidates[initialIndex]);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching spotlight brand:', err);
          setSpotlightBrand(null);
          setBrands([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; };
  }, [dayOfYear]);

  const handleDismiss = () => {
    if (spotlightBrand?.name) {
      setBrandDismissed(spotlightBrand.name);
    }
    setSpotlightBrand(null);
    setBrands([]);
  };

  const handleExplore = () => {
    if (spotlightBrand) {
      navigate(`/brand/${encodeURIComponent(spotlightBrand.name)}`);
    }
  };

  const cardStyle = {
    position: 'relative',
    marginBottom: '48px',
    background: '#fff',
    borderRadius: '12px',
    padding: '40px',
    border: '1px solid #eee',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
    width: '100%',
  };

  const responsiveStyles = `
    .brand-spotlight-wrap { padding: 0 16px; box-sizing: border-box; }
    .brand-spotlight-card { box-sizing: border-box; }
    .brand-spotlight-inner { 
      display: flex; 
      align-items: center; 
      gap: 40px; 
      flex-wrap: wrap;
      transition: opacity 0.3s ease-in-out;
    }
    .brand-spotlight-inner.transitioning {
      opacity: 0.4;
    }
    .brand-spotlight-content { flex: 1; min-width: 0; }
    .brand-spotlight-image-wrap { flex-shrink: 0; }
    .brand-spotlight-image, .brand-spotlight-image-placeholder {
      width: 180px; height: 180px; border-radius: 12px;
    }
    .brand-spotlight-indicators {
      display: flex;
      gap: 8px;
      justify-content: center;
      margin-top: 24px;
    }
    .brand-spotlight-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #d4d4d4;
      transition: all 0.3s ease;
      cursor: pointer;
    }
    .brand-spotlight-dot.active {
      background: #1a1a1a;
      width: 24px;
      border-radius: 4px;
    }
    .brand-spotlight-dot:hover {
      background: #888;
    }
    @media (max-width: 768px) {
      .brand-spotlight-wrap { padding: 0 12px; }
      .brand-spotlight-card { padding: 24px 20px; margin-bottom: 32px; }
      .brand-spotlight-inner { flex-direction: column; align-items: flex-start; gap: 24px; text-align: left; }
      .brand-spotlight-content { min-width: 0; width: 100%; }
      .brand-spotlight-image, .brand-spotlight-image-placeholder {
        width: 120px; height: 120px;
      }
      .brand-spotlight-title { font-size: 22px !important; }
      .brand-spotlight-dismiss { top: 12px; right: 12px; }
    }
    @media (max-width: 480px) {
      .brand-spotlight-wrap { padding: 0 10px; }
      .brand-spotlight-card { padding: 20px 16px; margin-bottom: 24px; }
      .brand-spotlight-inner { gap: 20px; }
      .brand-spotlight-image, .brand-spotlight-image-placeholder {
        width: 100px; height: 100px;
      }
      .brand-spotlight-title { font-size: 20px !important; }
      .brand-spotlight-body { font-size: 14px !important; margin-bottom: 20px !important; }
      .brand-spotlight-dismiss { top: 10px; right: 10px; width: 28px; height: 28px; }
      .brand-spotlight-cta { width: 100%; }
      .brand-spotlight-indicators { margin-top: 16px; }
    }
    @keyframes brandSpotlightShimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `;

  if (loading) {
    return (
      <div className="brand-spotlight-wrap">
        <div className="brand-spotlight-card" style={cardStyle}>
          <div className="brand-spotlight-inner">
            <div
              className="brand-spotlight-image-placeholder"
              style={{
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'brandSpotlightShimmer 1.2s ease-in-out infinite',
              }}
            />
            <div className="brand-spotlight-content">
            <div
              style={{
                width: '80px',
                height: '14px',
                borderRadius: '20px',
                background: '#f0f0f0',
                marginBottom: '16px',
              }}
            />
            <div
              style={{
                width: '60%',
                height: '28px',
                borderRadius: '4px',
                background: '#f0f0f0',
                marginBottom: '12px',
              }}
            />
            <div
              style={{
                width: '90%',
                height: '16px',
                borderRadius: '4px',
                background: '#f0f0f0',
                marginBottom: '8px',
              }}
            />
            <div
              style={{
                width: '70%',
                height: '16px',
                borderRadius: '4px',
                background: '#f0f0f0',
                marginBottom: '28px',
              }}
            />
            <div
              style={{
                width: '140px',
                height: '44px',
                borderRadius: '25px',
                background: '#f0f0f0',
              }}
            />
            </div>
          </div>
        </div>
        <style>{responsiveStyles}</style>
      </div>
    );
  }

  // No brand in last 7 days or error: show nothing (no empty-state message)
  if (!spotlightBrand) {
    return null;
  }

  return (
    <div className="brand-spotlight-wrap">
      <div className="brand-spotlight-card" style={cardStyle}>
        <button
          type="button"
          className="brand-spotlight-dismiss"
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'transparent',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            zIndex: 10,
            color: '#888',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f9f7f5';
            e.currentTarget.style.color = '#1a1a1a';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#888';
          }}
          aria-label="Dismiss"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <div className={`brand-spotlight-inner ${isTransitioning ? 'transitioning' : ''}`}>
          <div className="brand-spotlight-image-wrap">
            {spotlightBrand.image ? (
              <img
                className="brand-spotlight-image"
                src={spotlightBrand.image}
                alt={spotlightBrand.name}
                style={{ objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/default-image.avif';
                }}
              />
            ) : (
              <div
                className="brand-spotlight-image brand-spotlight-image-placeholder"
                style={{
                  background: '#f9f7f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#888',
                  fontSize: '32px',
                  fontWeight: '400',
                }}
              >
                {spotlightBrand.name?.charAt(0)?.toUpperCase() || 'B'}
              </div>
            )}
          </div>

          <div className="brand-spotlight-content">
            <div
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                background: '#f9f7f5',
                color: '#1a1a1a',
                borderRadius: '20px',
                fontSize: '10px',
                fontWeight: '600',
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                marginBottom: '16px',
              }}
            >
              New Launch
            </div>
            <h2
              className="brand-spotlight-title"
              style={{
                fontSize: '28px',
                fontWeight: '400',
                color: '#1a1a1a',
                margin: '0 0 12px 0',
                lineHeight: '1.3',
                letterSpacing: '-0.5px',
              }}
            >
              {spotlightBrand.name}
            </h2>
            <p
              className="brand-spotlight-body"
              style={{
                fontSize: '15px',
                color: '#666',
                margin: '0 0 28px 0',
                lineHeight: '1.6',
                fontWeight: '300',
              }}
            >
              {(spotlightBrand.days_since_launch ?? 0) === 0
                ? 'Just launched today! '
                : (spotlightBrand.days_since_launch ?? 0) === 1
                ? 'Launched yesterday! '
                : `Launched ${spotlightBrand.days_since_launch ?? 0} days ago. `}
              {spotlightText.subtitle}
            </p>
            <button
              type="button"
              className="brand-spotlight-cta"
              onClick={handleExplore}
              style={{
                padding: '12px 28px',
                background: '#1a1a1a',
                color: '#fff',
                border: 'none',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: '400',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#1a1a1a';
              }}
            >
              Explore {spotlightBrand.name} →
            </button>
          </div>
        </div>

        {/* Slideshow indicators - only show if multiple brands */}
        {brands.length > 1 && (
          <div className="brand-spotlight-indicators">
            {brands.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`brand-spotlight-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentIndex(index);
                    setIsTransitioning(false);
                  }, 300);
                }}
                aria-label={`Go to brand ${index + 1}`}
                style={{ border: 'none', padding: 0 }}
              />
            ))}
          </div>
        )}
      </div>
      <style>{responsiveStyles}</style>
    </div>
  );
}

export default BrandSpotlight;
